import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Description from "../../../components/description/Description";
import { useDialog } from "../../../components/dialog/DialogContext";
import { Topic } from "../../../components/item-content-two/Topic";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { PermisssionCard } from "../../../components/permission/PermissionCard";
import { Button } from "../../../components/button/Button";
import { LOGINS } from "../../../constants/logins-account-types";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import UserLoginColumns from "../../../models/ColumnDefinitions/UserLoginColumns";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { Permission as PermissionModel } from '../../../models/Permission';
import { TabSummary } from "../../../models/TabSummary";
import SiteSearch from "../../../models/search/SiteSearch";
import { default as searchColumns, default as UserLoginSearch } from "../../../models/search/UserLoginSearch";
import { assignUnassignPermissions, getLoginNamesWithPagination, getUserLogins } from "../../../services/loginService";
import { getPermissionById, getPermissionNames, storePermission, } from "../../../services/permissionService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getErrorMessage, showError, showLoadItemError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import { useGlobalState } from "../../../Providers";
import { ROLES } from "../../../constants/preset-constants";
import { SPECIFIC_HELP_KEY } from "../../../constants/specific-help-constant";
import AssignModalTwo from "@/components/assignWizard/AssignModal";
import { PaginationModel } from "@/models/Pagination";
import AssignedTab from "@/components/assignWizard/AssignedTab";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/operators/permissions';
const itemPath = '/access-control/operators/permission/';

const Permission: FC = () => {
    const user = useSelector((state: IAppState) => (state.userInfo));
    const { translate } = useLocale();
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.logins), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: false, isActive: true },
    ];
    const [tabs, setTabs] = useState<TabSummary[]>([])
    const [busy, setBusy] = useState(false);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [currentItem, setCurrentItem] = useState<PermissionModel>();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const history = useHistory();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const dialog = useDialog();
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const { setSpecificHelpTopicKey } = useGlobalState() ?? { setSpecificHelpTopicKey: undefined };
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];
    
    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>, setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
            do {
                const userLogins = await getLoginNamesWithPagination({ filters: itemId ? appendFilter(itemId, 'Profile', 'profile.id', []) : [], pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(userLogins.rows.map(userLogin => userLogin.value.toString()));
                rows = rows.concat(userLogins.rows.map(row => ({ id: row.value, name: row.label })));
                pagination = userLogins.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled([...ids, LOGINS.ID_SYSDBA.toString()]);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const reloadPage = async () => {
        try {
            setTabs([]);
            if (itemId) {
                let permission = await getPermissionById(itemId, translate);
                setSelectedItem({label: permission.name, value: permission.id.toString()})
                setCurrentItem(permission);
                const updatedTabs = updateTabBadgeTotal(0, permission?.logins, initialTabs);
                setTabs(updatedTabs);
                setSpecificHelpTopicKey && setSpecificHelpTopicKey(getSpecificHelpTopicKey(parseInt(itemId)));
                setBreadcrumb([
                    { value: '/access-control/operators', displayName: translate.byKey(TranslationKeys.operators), disableClick: true },
                    { value: allItemsPath, displayName: translate.byKey(TranslationKeys.permissions) },
                    { value: itemPath + `${itemId}`, displayName: `${permission?.name ?? ''}` }
                ]);
            }
            else {
                setTabs([initialTabs[1]]);
            }
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const topic: Topic = {
        accessor: 'nameOriginal',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.permissions),
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];
        return selectedTabContent.content;
    };

    const savePermission = (values) => {
        const permission: PermissionModel = {
            ...currentItem,
            id: values.id,
            name: values.name,
            siteId: currentItem?.siteId ? currentItem?.siteId : siteId,
            description: values.description,
            pInheritsFromId: values.id ? currentItem?.pInheritsFromId : values.pInheritsFrom,
            profileTypeId: values.profileTypeId
        }

        setBusy(true);
        storePermission(permission)
            .then(async (value:any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(permission);
                toast.success(translate.byKey(TranslationKeys.permissionSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }
    
    const assignPermissions = (toBeAdded: string[]) => {
        setBusy(true);
        assignUnassignPermissions(itemId, toBeAdded).then(() => {
                toast.success(translate.byKey(TranslationKeys.success));
                reloadPage();
            }).catch(error => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(error),
                });
            }).finally(() => {
                setBusy(false);
            });
    }

    const getSpecificHelpTopicKey = (permissionId: number) => {
        if (permissionId === ROLES.ID_GUARD) {
            return SPECIFIC_HELP_KEY.GUARD_ROLE;
        }
        else if (permissionId === ROLES.ID_SITE_ADMIN) {
            return SPECIFIC_HELP_KEY.SITE_ADMIN_ROLE;
        }
        else if (permissionId === ROLES.ID_SYSTEM_OWNER) {
            return SPECIFIC_HELP_KEY.SYSTEM_OWNER_ROLE;
        }
        else if (permissionId === ROLES.ID_INSTALLER) {
            return SPECIFIC_HELP_KEY.INSTALLER_ROLE;
        }
        else if (permissionId === ROLES.ID_REPORTS) {
            return SPECIFIC_HELP_KEY.REPORTER_ROLE;
        }
        else if (permissionId === ROLES.ID_RECEPTION) {
            return SPECIFIC_HELP_KEY.RECEPTION_ROLE;
        }
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <AssignedTab
                key={`AssignedUserForPermission${itemId}`}
                id={itemId}
                dataMethod={(listParameters?: ListQuery) => getUserLogins({ ...listParameters, filters: appendFilter(itemId, "Profile", "profile.id", []) }, parseInt(user.profileId))}
                column={UserLoginColumns}
                searchColumn={UserLoginSearch}
                lookup={lookup}
                title={translate.byKey(TranslationKeys.logins)}>
                <Button variant="submit" shape="rounded" id="manageBtn" className="ml-8 py-1 px-5" onClick={() => {setShowManagedModal(true);}}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ]

    return <div className="flex h-full">
    <TopicContextProvider
        itemId={itemId}
        topic={topic}
        dataMethod={listParamters => getPermissionNames(listParamters, currentItem?.profileTypeId, parseInt(user.profileId))}
        selectedItem={selectedItem}>
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={savePermission}
                tabs={tabs}
                initialValues={currentItem}>
                <PermisssionCard />
            </SimpleItemDetails> 
        </ItemContentTabTwo>
    </TopicContextProvider>
        <AssignModalTwo
            isOpen={showManagedModal}
            onClose={() => setShowManagedModal(false)}
            assigningTitle={translate.byKey(TranslationKeys.permissions)}
            lookup={lookup}
            dataMethod={listparameters => getUserLogins(listparameters, parseInt(user.profileId))}
            tableColumns={UserLoginColumns}
            searchColumns={searchColumns}
            handleSubmit={assignPermissions}
            getAssignedItems={getAssignedItems}
            modalTitle={translate.byKey(TranslationKeys.assignPermissions)}/>
    </div>
}

export default Permission;
